import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  BarChart3,
  ChevronLeft,
  ChevronRight,
  EyeOff,
  MessageSquareText,
  Search,
} from "lucide-react";

import { cleanSlideText } from "./briefingAnswers";
import MarkdownTable from "./MarkdownTable";
import type { ParsedDocumentPage } from "../../Types/AccountPlan";

interface Props {
  pages: ParsedDocumentPage[];
  /** 1-based slide to show; lets citations elsewhere jump to a slide. */
  selectedPage?: number | null;
  onSelectPage?: (pageNumber: number) => void;
  /** Image links are short-lived; called (once per page) when one has expired. */
  onImagesExpired?: () => void;
}

const API_BASE = (process.env.REACT_APP_BASE_URL || "").replace(/\/$/, "");

/** Page images are presigned S3 URLs; an API-relative link is resolved against the API host. */
export const resolveAssetUrl = (url?: string | null): string | null =>
  !url ? null : url.startsWith("/") ? `${API_BASE}${url}` : url;

const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const slideTitle = (p: ParsedDocumentPage) =>
  p.heading?.trim() || `Slide ${p.page_number}`;

const slideMatches = (p: ParsedDocumentPage, q: string) => {
  if (!q) return false;
  const haystack = [
    p.heading,
    p.full_text,
    p.notes,
    ...(p.tables || []),
    ...(p.key_points || []),
  ]
    .filter(Boolean)
    .join("\n")
    .toLowerCase();
  return haystack.indexOf(q) !== -1;
};

/** Wrap case-insensitive matches of `query` in <mark>. */
const Highlighted: React.FC<{ text: string; query: string }> = ({
  text,
  query,
}) => {
  if (!query) return <>{text}</>;
  const parts = text.split(new RegExp(`(${escapeRegex(query)})`, "gi"));
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <mark
            key={i}
            className="rounded-sm bg-amber-200 px-0.5 font-semibold"
          >
            {part}
          </mark>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        )
      )}
    </>
  );
};

/** Pages whose expired image already triggered a refresh, per viewer. */
const ExpiredContext = React.createContext<{
  retried: Set<number>;
  onExpired?: () => void;
}>({ retried: new Set() });

/** An image URL. On the first failure for a page it asks for fresh links (they
 * expire after an hour); after that it falls back to text — once per page, so a
 * broken image can't loop. */
const useSlideImage = (page: ParsedDocumentPage) => {
  const src = resolveAssetUrl(page.image_url);
  const { retried, onExpired } = React.useContext(ExpiredContext);
  const [failed, setFailed] = useState(false);
  // Until the picture has actually drawn, the text card shows (UI tour,
  // 2026-11-03: pictures that never arrived left blank white boxes).
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    setFailed(false);
    setLoaded(false);
  }, [src]);
  const onError = () => {
    setLoaded(false);
    if (onExpired && !retried.has(page.page_number)) {
      retried.add(page.page_number);
      onExpired();
      return;
    }
    setFailed(true);
  };
  const onLoad = (e: React.SyntheticEvent<HTMLImageElement>) =>
    setLoaded(e.currentTarget.naturalWidth > 0);
  return { src: failed ? null : src, onError, onLoad, loaded };
};

/** Shown wherever a slide has no picture to draw. */
export const NO_PICTURE = "Slide picture not available";

const SlideThumb: React.FC<{
  page: ParsedDocumentPage;
  selected: boolean;
  matched: boolean;
  dimmed: boolean;
  onSelect: () => void;
}> = ({ page, selected, matched, dimmed, onSelect }) => {
  const ref = useRef<HTMLButtonElement>(null);
  const { src, onError, onLoad, loaded } = useSlideImage(page);

  useEffect(() => {
    if (selected) ref.current?.scrollIntoView({ block: "nearest" });
  }, [selected]);

  return (
    <button
      ref={ref}
      type="button"
      onClick={onSelect}
      aria-current={selected ? "true" : undefined}
      aria-label={`Slide ${page.page_number}: ${slideTitle(page)}`}
      className={`group relative aspect-video overflow-hidden rounded-md border text-left transition ${
        selected
          ? "border-[#974ae4] ring-[3px] ring-[#974ae4]/20"
          : matched
            ? "border-amber-400 ring-2 ring-amber-100"
            : "border-gray-200 hover:-translate-y-0.5 hover:border-[#974ae4] hover:shadow-[0_6px_14px_-4px_rgba(124,58,237,0.2)]"
      } ${dimmed ? "opacity-40" : ""} ${src && loaded ? "bg-white" : "bg-gradient-to-br from-slate-50 to-slate-200"}`}
    >
      {!(src && loaded) && (
        // The text card: no picture, or not drawn yet. Never a blank box.
        <span
          data-testid="slide-thumb-placeholder"
          title={NO_PICTURE}
          className="absolute inset-0"
        >
          <span className="absolute left-1.5 top-1 text-[10px] font-bold text-slate-400">
            {page.page_number}
          </span>
          <span className="absolute inset-x-2 bottom-1.5 line-clamp-2 text-center text-[10px] font-semibold leading-tight text-slate-700">
            {slideTitle(page)}
          </span>
        </span>
      )}
      {src && (
        <>
          {/* Lazy: a plan can run to 300 pages; only thumbnails in view load. */}
          <img
            src={src}
            alt=""
            loading="lazy"
            decoding="async"
            onError={onError}
            onLoad={onLoad}
            className={`relative h-full w-full object-contain ${loaded ? "" : "opacity-0"}`}
          />
          {loaded && (
            <span className="absolute left-1 top-1 rounded bg-white/90 px-1 text-[10px] font-bold leading-4 text-slate-600 shadow-sm">
              {page.page_number}
            </span>
          )}
        </>
      )}
      {page.hidden && (
        <EyeOff
          className="absolute right-1 top-1 h-3 w-3 rounded-sm bg-white/90 text-gray-500"
          aria-label="Hidden slide"
        />
      )}
    </button>
  );
};

const SlideStage: React.FC<{ page: ParsedDocumentPage }> = ({ page }) => {
  const { src, onError, onLoad, loaded } = useSlideImage(page);
  const card = (
    <div
      data-testid="slide-stage-placeholder"
      className="max-w-2xl px-6 text-center"
    >
      <div className="mb-3 font-mono text-[10px] uppercase tracking-widest text-purple-500">
        Slide {page.page_number}
      </div>
      <div className="mb-4 text-2xl font-bold leading-tight text-gray-900">
        {slideTitle(page)}
      </div>
      <div className="text-xs text-gray-500">
        {NO_PICTURE}. See the extracted text below.
      </div>
    </div>
  );

  if (src) {
    return (
      <>
        {!loaded && card}
        <img
          src={src}
          alt={`Slide ${page.page_number}: ${slideTitle(page)}`}
          onError={onError}
          onLoad={onLoad}
          className={
            loaded
              ? "h-full w-full object-contain"
              : "absolute h-0 w-0 opacity-0"
          }
        />
      </>
    );
  }
  return card;
};

const DetailSection: React.FC<{
  label: string;
  icon?: React.ElementType;
  children: React.ReactNode;
}> = ({ label, icon: Icon, children }) => (
  <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
    <h4 className="mb-2 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-gray-400">
      {Icon && <Icon className="h-3.5 w-3.5" aria-hidden />}
      {label}
    </h4>
    {children}
  </section>
);

const SlideViewer: React.FC<Props> = ({
  pages,
  selectedPage,
  onSelectPage,
  onImagesExpired,
}) => {
  const viewerRef = useRef<HTMLDivElement>(null);
  const retried = useRef(new Set<number>()).current;
  const expired = useMemo(
    () => ({ retried, onExpired: onImagesExpired }),
    [retried, onImagesExpired]
  );
  // Text as people read it: internal [[…]] markers never show (UI tour item 10).
  const slides = useMemo(
    () =>
      pages
        .slice()
        .sort((a, b) => a.page_number - b.page_number)
        .map(p => ({
          ...p,
          heading: cleanSlideText(p.heading),
          full_text: cleanSlideText(p.full_text),
          notes: cleanSlideText(p.notes),
          key_points: p.key_points
            ?.map(k => cleanSlideText(k))
            .filter(k => k.trim() !== ""),
        })),
    [pages]
  );
  const indexOfPage = (n?: number | null) =>
    Math.max(
      0,
      slides.findIndex(s => s.page_number === n)
    );
  const [index, setIndexState] = useState(() => indexOfPage(selectedPage));
  const indexRef = useRef(index);
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();

  // Side effects stay out of the state updater (StrictMode calls those twice).
  const setIndex = (next: number | ((i: number) => number)) => {
    const value = typeof next === "function" ? next(indexRef.current) : next;
    indexRef.current = value;
    setIndexState(value);
    const page = slides[value]?.page_number;
    if (page && onSelectPage) onSelectPage(page);
  };

  // A citation elsewhere (Sponsor View) asked for a specific slide.
  useEffect(() => {
    if (selectedPage) {
      indexRef.current = indexOfPage(selectedPage);
      setIndexState(indexRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPage, slides]);

  const matches = useMemo(
    () =>
      slides.reduce<Record<number, boolean>>((acc, s) => {
        if (slideMatches(s, q)) acc[s.page_number] = true;
        return acc;
      }, {}),
    [slides, q]
  );
  const matchCount = Object.keys(matches).length;

  const go = (delta: number) =>
    setIndex(i => (i + delta + slides.length) % slides.length);

  // Enter in the search box jumps to the next matching slide.
  const nextMatch = () => {
    if (!matchCount) return;
    for (let step = 1; step <= slides.length; step++) {
      const i = (index + step) % slides.length;
      if (matches[slides[i].page_number]) {
        setIndex(i);
        return;
      }
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      // Only when nothing else has focus, or focus is inside the viewer — never
      // steal arrows from editable text, radio groups, tabs or other widgets.
      if (target?.isContentEditable) return;
      const role = target?.getAttribute?.("role");
      if (role === "radio" || role === "tab" || role === "slider") return;
      const active = document.activeElement;
      const free = !active || active === document.body;
      if (!free && !viewerRef.current?.contains(active)) return;
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slides.length]);

  // Details scroll on their own; start each slide at its top.
  const detailsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    detailsRef.current?.scrollTo?.({ top: 0 });
  }, [index]);

  if (slides.length === 0) {
    return <p className="text-sm text-gray-500">No slides were returned.</p>;
  }

  const current = slides[Math.min(index, slides.length - 1)];
  const tables = current.tables || [];
  const visuals = current.visual_elements || [];
  const points = current.key_points || [];

  return (
    <ExpiredContext.Provider value={expired}>
      <div
        ref={viewerRef}
        className="grid w-full grid-cols-1 gap-5 lg:grid-cols-12"
      >
        {/* Search + thumbnail rail */}
        <div className="space-y-3 lg:col-span-3">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
              aria-hidden
            />
            <input
              type="search"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === "Enter" && nextMatch()}
              placeholder="Search slide text…"
              aria-label="Search slide text"
              className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-200"
            />
          </div>
          {q && (
            <p className="text-xs text-gray-500" aria-live="polite">
              {matchCount === 0
                ? "No matches"
                : `${matchCount} matching slide${matchCount === 1 ? "" : "s"} · Enter for next`}
            </p>
          )}
          <div className="grid max-h-[640px] grid-cols-3 gap-2 overflow-y-auto p-1 lg:grid-cols-2">
            {slides.map((s, i) => (
              <SlideThumb
                key={s.page_number}
                page={s}
                selected={i === index}
                matched={!!matches[s.page_number]}
                dimmed={!!q && matchCount > 0 && !matches[s.page_number]}
                onSelect={() => setIndex(i)}
              />
            ))}
          </div>
        </div>

        {/* Selected slide */}
        <div className="min-w-0 space-y-4 lg:col-span-9">
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-4 py-2.5">
              <div className="flex min-w-0 items-center gap-3 text-xs">
                <span className="shrink-0 rounded bg-purple-100 px-2 py-0.5 font-mono font-semibold text-purple-800">
                  SLIDE {current.page_number}
                </span>
                <span className="truncate font-medium text-gray-700">
                  {slideTitle(current)}
                </span>
                {current.hidden && (
                  <span className="shrink-0 rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-gray-600">
                    Hidden
                  </span>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <span className="mr-1 text-xs text-gray-400">
                  {index + 1} / {slides.length}
                </span>
                <button
                  type="button"
                  onClick={() => go(-1)}
                  aria-label="Previous slide"
                  className="rounded p-1.5 text-gray-500 hover:bg-white"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => go(1)}
                  aria-label="Next slide"
                  className="rounded p-1.5 text-gray-500 hover:bg-white"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="flex aspect-video items-center justify-center bg-gradient-to-br from-white to-gray-50 p-4 lg:max-h-[50vh] lg:w-full">
              <SlideStage page={current} />
            </div>
          </div>

          <div
            ref={detailsRef}
            data-testid="slide-details"
            className="space-y-4 lg:max-h-[26vh] lg:min-h-[140px] lg:overflow-y-auto lg:pr-1"
          >
            <DetailSection label="Extracted slide text">
              <p className="whitespace-pre-line text-sm leading-relaxed text-gray-700">
                {current.full_text ? (
                  <Highlighted text={current.full_text} query={q} />
                ) : (
                  <span className="text-gray-400">No text on this slide.</span>
                )}
              </p>
            </DetailSection>

            {current.notes && (
              <DetailSection label="Speaker notes" icon={MessageSquareText}>
                <p className="whitespace-pre-line text-sm leading-relaxed text-gray-700">
                  <Highlighted text={current.notes} query={q} />
                </p>
              </DetailSection>
            )}

            {tables.length > 0 && (
              <DetailSection label="Tables">
                <div className="space-y-2">
                  {tables.map((t, i) => (
                    <MarkdownTable key={i} markdown={t} />
                  ))}
                </div>
              </DetailSection>
            )}

            {visuals.length > 0 && (
              <DetailSection label="Visual elements" icon={BarChart3}>
                <ul className="space-y-1.5">
                  {visuals.map((v, i) => (
                    <li
                      key={i}
                      className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-700"
                    >
                      {v}
                    </li>
                  ))}
                </ul>
              </DetailSection>
            )}

            {points.length > 0 && (
              <DetailSection label="Key points">
                <ul className="list-disc space-y-1 pl-5 text-sm text-gray-800">
                  {points.map((p, i) => (
                    <li key={i}>
                      <Highlighted text={p} query={q} />
                    </li>
                  ))}
                </ul>
              </DetailSection>
            )}
          </div>
        </div>
      </div>
    </ExpiredContext.Provider>
  );
};

export default SlideViewer;
